/* eslint-disable @typescript-eslint/no-misused-promises */
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { BaseBillingTermMilestone, BillingPlanTerm } from "@/types";
import { useMakeMilestonePayment } from "./mutation";
import {
  milestonePaymentFormSchema,
  MilestonePaymentFormSchema,
} from "./schema";

const PayMilestonesForm = ({
  milestoneOptions,
  onCancel,
}: {
  milestoneOptions: (BillingPlanTerm & BaseBillingTermMilestone)[];
  onCancel: () => void;
}) => {
  const {
    makeMilestonePayment,
    isMakingMilestonePayment,
    makeMilestonePaymentError,
  } = useMakeMilestonePayment();

  const form = useForm<MilestonePaymentFormSchema>({
    resolver: async (data, context, opts) => {
      return zodResolver(milestonePaymentFormSchema)(data, context, opts);
    },
  });

  function onSubmit(data: MilestonePaymentFormSchema) {
    if (isMakingMilestonePayment) {
      return;
    }

    makeMilestonePayment({ billingTermId: data.milestone });
  }

  return (
    <Form {...form}>
      {makeMilestonePaymentError && (
        <p className="text-center w-full border border-destructive rounded-md text-destructive p-2 bg-destructive/10">
          Something went wrong when attempting to check out, please try again
          later.
        </p>
      )}
      <form className="space-y-4 w-full" onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          control={form.control}
          name="milestone"
          render={({ field }) => (
            <FormItem>
              <FormLabel required>Milestone</FormLabel>
              <FormControl>
                <Select
                  name={field.name}
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={field.disabled}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a milestone" />
                  </SelectTrigger>
                  <SelectContent>
                    {milestoneOptions.map((milestone) => (
                      <SelectItem
                        key={`milestone-${milestone.id}`}
                        value={milestone.id}
                      >
                        {milestone.milestoneLabel} (
                        {new Intl.NumberFormat("en-US", {
                          style: "currency",
                          currency: milestone.currency,
                        }).format(milestone.totalAmountCents / 100)}{" "}
                        + tax)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="h-[1px] bg-[#E4E7EC] w-full" />
        <div className="!mb-1 flex justify-between">
          <Button variant="stroke" className="mt-2" onClick={onCancel}>
            Cancel
          </Button>

          <Button
            variant="success"
            type="submit"
            className="mt-2"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={form.handleSubmit(onSubmit)}
            disabled={isMakingMilestonePayment}
          >
            Confirm and Pay
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default PayMilestonesForm;
